import { ListRenderer } from "@/components/list/list-renderer";
import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import { useRemoveDislikedMeal } from "@/features/meal-plan/hooks/use-remove-disliked-meal";
import { useRemoveFavouriteMeal } from "@/features/meal-plan/hooks/use-remove-favourite-meal";
import { cn } from "@/utils/cn";
import { MealPreferenceListEmpty } from "./empty-state";
import { MealPreferenceListLoading } from "./loading-state";
import { MealPreferenceItem } from "./meal-preference-item";

type MealPreferenceListProps =
  | {
      meals: Meal[];
      emptyMessage: string;
      isLoading?: boolean;
      className?: string;
      variant: "like";
      mealTime: MealTime;
    }
  | {
      meals: Meal[];
      emptyMessage: string;
      isLoading?: boolean;
      className?: string;
      variant: "dislike";
      mealTime?: never;
    };

export const MealPreferenceList = (props: MealPreferenceListProps) => {
  const { meals, emptyMessage, isLoading = false, className, variant } = props;
  const { removeFavouriteMeal, isRemovingFavouriteMeal } =
    useRemoveFavouriteMeal();
  const { removeDislikedMeal, isRemovingDislikedMeal } =
    useRemoveDislikedMeal();
  const isLikeVariant = variant === "like";
  const likeMealTime = isLikeVariant ? props.mealTime : undefined;
  const isRemovingMeal = isLikeVariant
    ? isRemovingFavouriteMeal
    : isRemovingDislikedMeal;
  const handleRemoveMeal = (meal: Meal) => {
    if (!meal?.id) {
      return;
    }

    if (isLikeVariant) {
      if (!likeMealTime) {
        return;
      }
      removeFavouriteMeal({
        meal_id: meal.id,
        meal_time: likeMealTime,
      });
      return;
    }

    removeDislikedMeal({
      meal_id: meal.id,
    });
  };

  return (
    <ListRenderer
      items={meals}
      isLoading={isLoading}
      loadingComponent={<MealPreferenceListLoading className={className} />}
      emptyComponent={
        <MealPreferenceListEmpty message={emptyMessage} className={className} />
      }
    >
      <div className={cn("flex flex-col gap-2.5 pb-safe", className)}>
        {meals.map((meal) => (
          <MealPreferenceItem
            key={meal.id ?? meal.name}
            meal={meal}
            variant={variant}
            isRemoving={isRemovingMeal}
            onRemove={handleRemoveMeal}
          />
        ))}
      </div>
    </ListRenderer>
  );
};
